import { tryCashPosture } from '../modules/cashflow.js';
// liabilities.js — Bulk multi-currency debt tracking
// Isolated from portfolio, monthly, and trades — only affects Cash and Dashboard net worth

import { S, save, stampItem, tombstoneId } from '../core/state.js';
import { uid, now, f, fU, fT, esc } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';

let editLiabId = null;
let payLiabId = null;

// ── FX helpers ──────────────────────────────────────────────────
// Converts liability currency amount to TRY
/**
 * Liability amount -> TRY, or null when no usable rate exists.
 *
 * The old `|| 0` valued USD/EUR debt at ZERO, so totalLiabilitiesUSD() dropped it, net
 * worth was overstated by the full debt, and the Goals FI gate reported the user
 * debt-free. Returning null lets callers decide, rather than silently erasing a
 * liability. (audit 2026-08-01)
 */
function liabToTRY(amount, currency) {
  if (!amount || amount <= 0) return 0;
  switch (currency) {
    case 'TRY': return amount;
    case 'USD': return S.fxRate > 0 ? amount * S.fxRate : null;
    case 'EUR': return S.fxRateEUR > 0 ? amount * S.fxRateEUR : null;
    case 'XAU': return amount * (S.prices?.XAU?.price || 0);
    case 'XAG': return amount * (S.prices?.XAG?.price || 0);
    default: return 0;
  }
}

// Converts liability currency amount to USD
function liabToUSD(amount, currency) {
  const tryVal = liabToTRY(amount, currency);
  if (tryVal == null) return null;
  return S.fxRate > 0 ? tryVal / S.fxRate : null;
}

// Gets current FX rate for converting TRY → liability currency
function liabFxRate(currency) {
  switch (currency) {
    case 'TRY': return 1;
    case 'USD': return S.fxRate || 0;
    case 'EUR': return S.fxRateEUR || 0;
    case 'XAU': return S.prices?.XAU?.price || 0;
    case 'XAG': return S.prices?.XAG?.price || 0;
    default: return 0;
  }
}

// Format by liability currency
function liabFmt(amount, currency) {
  switch (currency) {
    case 'TRY': return fT(amount);
    case 'USD': return fU(amount);
    case 'EUR': return '€' + f(amount, 2);
    case 'XAU': return f(amount, 4) + 'g Au';
    case 'XAG': return f(amount, 2) + 'g Ag';
    default: return f(amount, 2);
  }
}

// ── CRUD ────────────────────────────────────────────────────────
function openLiability(id) {
  editLiabId = id || null;
  const d = id ? S.liabilities.find(x => x.id === id) : null;
  document.getElementById('liabModTitle').textContent = d ? 'Edit Liability' : 'Add Liability';
  document.getElementById('liabName').value = d ? d.name : '';
  document.getElementById('liabCurrency').value = d ? d.currency : 'USD';
  document.getElementById('liabAmount').value = d ? d.originalAmount : '';
  document.getElementById('liabNote').value = d ? d.note || '' : '';
  document.getElementById('liabDelBtn').style.display = id ? '' : 'none';
  // If editing, disable currency & amount if payments already made
  const hasPayments = d && d.payments && d.payments.length > 0;
  document.getElementById('liabCurrency').disabled = hasPayments;
  document.getElementById('liabAmount').disabled = hasPayments;
  document.getElementById('moLiab').classList.add('active');
}

function saveLiability() {
  const name = document.getElementById('liabName').value.trim();
  if (!name) return toast('Name required', 'a-r');
  const currency = document.getElementById('liabCurrency').value;
  const amount = parseFloat(document.getElementById('liabAmount').value);
  if (!amount || amount <= 0) return toast('Valid amount required', 'a-r');
  const note = document.getElementById('liabNote').value.trim();

  if (editLiabId) {
    const idx = S.liabilities.findIndex(x => x.id === editLiabId);
    if (idx >= 0) {
      const d = S.liabilities[idx];
      d.name = name;
      d.note = note;
      // If no payments yet, allow amount/currency change
      if (!d.payments || d.payments.length === 0) {
        d.currency = currency;
        d.originalAmount = amount;
        d.remainingAmount = amount;
      }
      stampItem(d);
    }
  } else {
    const liab = {
      id: uid(),
      name,
      currency,
      originalAmount: amount,
      remainingAmount: amount,
      createdAt: now(),
      note,
      status: 'open',
      payments: []
    };
    stampItem(liab);
    S.liabilities.push(liab);
    // Log to history
    if (window.lg) window.lg('liability', name, 'Created: ' + liabFmt(amount, currency) + ' ' + currency);
  }

  editLiabId = null;
  save();
  clMo('moLiab');
  renderLiabilities();
  toast(name + ' saved', 'a-b');
}

/**
 * Undo one liability payment — the door the Cash page's "Liabilities" badge names.
 *
 * Until 2026-08-14 this did not exist. `payLiability` pushed a payment, reduced
 * `remainingAmount`, debited `S.cash.TRY` and wrote a ledger row; NOTHING removed one, the
 * payments were never rendered, and `deleteLiability` dropped the record with every payment
 * inside it while touching neither the balance nor the ledger. `4e187ee` meanwhile hid the Del
 * button on both rows — ₺777,875 and ₺683,404.84 — and told him to come here.
 *
 * All four legs move together, or none do: the balance, the ledger row, the payment record and
 * the remaining amount. It REFUSES rather than half-undoing when it cannot identify the row.
 */
async function deletePayment(liabId, paymentId) {
  const d = (S.liabilities || []).find(x => x && x.id === liabId);
  if (!d) return toast('Liability not found', 'a-r');
  const pi = (d.payments || []).findIndex(p => p && p.id === paymentId);
  if (pi < 0) return toast('Payment not found', 'a-r');
  const p = d.payments[pi];
  const tryEquiv = Number(p.tryEquiv) || 0;

  // The ledger row is found by LINK, never by note. v51 back-linked every legacy row it could
  // identify; anything it refused to guess at is refused here too, because reversing the balance
  // while some other row stays behind is the one-legged write pointing backwards (rule #25).
  const rows = (S.cashTxs || []).filter(tx => tx && tx._paymentId === paymentId);
  if (rows.length !== 1) {
    return toast(
      rows.length === 0
        ? 'This payment has no linked ledger row, so it cannot be reversed safely. Delete the row and the payment by hand, or restore the link.'
        : `This payment matches ${rows.length} ledger rows — refusing to guess which one to reverse.`,
      'a-r');
  }

  if (!await uiConfirm(
    `Undo this payment of ${liabFmt(p.amount, d.currency)} (${fT(tryEquiv)} TRY)? `
    + 'Cash goes back up, the ledger row is removed, and the remaining balance is restored.',
    { title: 'Undo payment', okText: 'Undo', danger: true })) return;

  // 1. the balance
  if (!S.cash) S.cash = { USD: 0, TRY: 0, EUR: 0 };
  S.cash.TRY = (S.cash.TRY || 0) + tryEquiv;
  // 2. the ledger row — tombstoned so a merge cannot resurrect it over the restored balance
  tombstoneId(rows[0].id);
  S.cashTxs = S.cashTxs.filter(tx => tx.id !== rows[0].id);
  // 3. the payment record
  d.payments.splice(pi, 1);
  // 4. the remaining amount, and the status that was derived from it
  d.remainingAmount = Math.min(
    Number(d.originalAmount) || Infinity,
    (Number(d.remainingAmount) || 0) + (Number(p.amount) || 0));
  if (d.remainingAmount > 0.001 && d.status === 'closed') d.status = 'open';
  stampItem(d);

  save();
  if (window.lg) window.lg('liability', d.name, 'Payment undone: ' + liabFmt(p.amount, d.currency) + ' (' + fT(tryEquiv) + ' TRY)');
  renderLiabilities();
  if (window.renderC) window.renderC();
  if (window.markDirty) window.markDirty('dash', 'cash');
  toast('Payment undone — ' + fT(tryEquiv) + ' TRY returned to cash', 'a-b');
}

async function deleteLiability() {
  if (!editLiabId) return;
  const d = S.liabilities.find(x => x.id === editLiabId);
  // A liability carrying payments is not a record, it is a record AND the money that left the
  // book because of it. Deleting it used to drop both and touch neither the balance nor the
  // ledger, orphaning rows that still told him to come here to undo them. Undo the payments
  // first — that path moves all four legs — and then the empty record can go. (audit 2026-08-14)
  if ((d?.payments || []).length) {
    return toast(
      `"${d.name}" has ${d.payments.length} payment(s) totalling ${fT((d.payments || []).reduce((s, p) => s + (Number(p.tryEquiv) || 0), 0))} TRY. `
      + 'Undo them first — deleting the liability would leave that money missing from cash with no record of why.',
      'a-r');
  }
  if (!await uiConfirm('Delete liability "' + (d?.name || '') + '"? This cannot be undone.', { title: 'Delete liability', okText: 'Delete', danger: true })) return;
  tombstoneId(editLiabId);
  S.liabilities = S.liabilities.filter(x => x.id !== editLiabId);
  editLiabId = null;
  save();
  clMo('moLiab');
  renderLiabilities();
  toast('Deleted', 'a-b');
}

function clMoLiab() { editLiabId = null; clMo('moLiab'); }

// ── PAY FLOW ────────────────────────────────────────────────────
function openLiabPay(id) {
  const d = S.liabilities.find(x => x.id === id);
  if (!d || d.status === 'closed') return toast('Liability already closed', 'a-r');
  payLiabId = id;

  document.getElementById('liabPayCur').textContent = d.currency;
  document.getElementById('liabPayAmt').value = d.remainingAmount;
  document.getElementById('liabPayFx').value = '';
  document.getElementById('liabPayNote').value = '';

  const fxRate = liabFxRate(d.currency);
  const tryEquiv = liabToTRY(d.remainingAmount, d.currency);
  document.getElementById('liabPayInfo').innerHTML =
    '<strong>' + esc(d.name) + '</strong> — Remaining: ' + liabFmt(d.remainingAmount, d.currency) +
    (fxRate ? ' ≈ ' + fT(tryEquiv) : '') +
    '<br>Cash (TRY): ' + fT(_tryNow()) + _liabCommittedNote();

  _updatePayPreview();
  document.getElementById('moLiabPay').classList.add('active');

  // Live preview on input
  document.getElementById('liabPayAmt').oninput = _updatePayPreview;
  document.getElementById('liabPayFx').oninput = _updatePayPreview;
}

/**
 * The one screen that asks "can I afford this right now" was reading the raw balance.
 * On 2026-08-08 that meant a ₺200,000 payment against ₺336,800 rendered green with no
 * warning, while ₺296,030 of the balance was this month's bills and the IBKR transfer.
 */
function _tryNow() { return tryCashPosture().current; }

function _liabCommittedNote() {
  const p = tryCashPosture();
  if (!p.ok) return ` <span style="color:var(--t3);font-size:11px">(committed portion unknown — ${p.why === 'no-month' ? 'this month not opened yet' : 'not reconciled'})</span>`;
  if (p.committed <= 0) return '';
  return ' <span style="color:var(--ora);font-size:11px">— ' + fT(p.committed) + ' already committed, ' + fT(p.free) + ' free</span>';
}

function _updatePayPreview() {
  if (!payLiabId) return;
  const d = S.liabilities.find(x => x.id === payLiabId);
  if (!d) return;
  const amt = parseFloat(document.getElementById('liabPayAmt').value) || 0;
  const fxOverride = parseFloat(document.getElementById('liabPayFx').value) || 0;
  const fxRate = fxOverride || liabFxRate(d.currency);
  const tryDeduct = amt * fxRate;
  // `S.cash.TRY` is the stored ledger; bills confirmed since the last bank reading have not
  // left it. Comparing a payment against it while comparing the free figure against `current`
  // put two balances ₺90,994 apart on one screen and printed "this covers" over a ₺54,194
  // overdraft. One basis: `current`.
  const _posture = tryCashPosture();
  const cashTRY = _posture.current;
  const after = cashTRY - tryDeduct;
  const afterFree = _posture.ok ? _posture.free - tryDeduct : null;

  let html = '';
  if (fxRate > 0) {
    html = 'FX Rate: 1 ' + d.currency + ' = ' + f(fxRate, 4) + ' TRY<br>';
    html += 'TRY to deduct from Cash: <strong>' + fT(tryDeduct) + '</strong><br>';
    html += 'Cash TRY after: <strong style="color:' + (after >= 0 ? 'var(--grn)' : 'var(--red)') + '">' + fT(after) + '</strong>';
    if (afterFree != null) {
      html += '<br>Free after: <strong style="color:' + (afterFree >= 0 ? 'var(--grn)' : 'var(--red)') + '">' + fT(afterFree) + '</strong>'
        + ' <span style="color:var(--t3);font-size:11px">(after ' + fT(_posture.committed) + ' already committed this month)</span>';
    }
    if (after < 0) html += '<br><span style="color:var(--red)">Warning: insufficient TRY balance</span>';
    else if (afterFree != null && afterFree < 0) html += '<br><span style="color:var(--ora)">Warning: this covers, but only by using money already committed this month</span>';
    else if (afterFree == null) html += '<br><span style="color:var(--t3);font-size:11px">' + (_posture.why === 'no-month' ? 'This month has no cash-flow record yet' : 'TRY cash has not been reconciled') + ' — the committed portion is unknown</span>';
  } else {
    html = '<span style="color:var(--ora)">No FX rate available — enter manual rate</span>';
  }
  document.getElementById('liabPayPreview').innerHTML = html;
}

function execLiabPay() {
  const d = S.liabilities.find(x => x.id === payLiabId);
  if (!d) return toast('Liability not found', 'a-r');

  const amt = parseFloat(document.getElementById('liabPayAmt').value);
  if (!amt || amt <= 0) return toast('Enter a valid amount', 'a-r');
  if (amt > d.remainingAmount * 1.001) return toast('Cannot pay more than remaining (' + liabFmt(d.remainingAmount, d.currency) + ')', 'a-r');

  const fxOverride = parseFloat(document.getElementById('liabPayFx').value) || 0;
  const fxRate = fxOverride || liabFxRate(d.currency);
  if (!fxRate || fxRate <= 0) return toast('No FX rate available — enter manual rate', 'a-r');

  const tryDeduct = amt * fxRate;
  const note = document.getElementById('liabPayNote').value.trim();

  // Record payment
  const payment = {
    id: uid(),
    date: now(),
    amount: amt,
    fxRate: fxRate,
    tryEquiv: tryDeduct,
    note: note || 'Payment'
  };
  if (!d.payments) d.payments = [];
  d.payments.push(payment);

  // Reduce remaining
  d.remainingAmount = Math.max(0, d.remainingAmount - amt);
  if (d.remainingAmount < 0.001) {
    d.remainingAmount = 0;
    d.status = 'closed';
  }
  stampItem(d);

  // Deduct from Cash TRY
  if (!S.cash) S.cash = { USD: 0, TRY: 0, EUR: 0 };
  S.cash.TRY = (S.cash.TRY || 0) - tryDeduct;

  // Record cash transaction
  const tx = {
    id: uid(),
    date: now(),
    type: 'withdraw',
    amount: -tryDeduct,
    currency: 'TRY',
    note: 'Liability payment: ' + d.name + ' — ' + liabFmt(amt, d.currency),
    // PROVENANCE ON THE FORWARD PATH. `deletePayment` reverses `p.tryEquiv` and removes this
    // row; without the link it would have to match on a note, which is prose. The same lesson
    // MOVE-002 is still open on: an inverse cannot know what to undo unless the forward path
    // wrote it down. Legacy rows are back-linked by migration v51.
    _liabId: d.id,
    _paymentId: payment.id
  };
  stampItem(tx);
  S.cashTxs.unshift(tx);

  // History log
  if (window.lg) window.lg('liability', d.name, 'Payment: ' + liabFmt(amt, d.currency) + ' (' + fT(tryDeduct) + ' TRY)' + (d.status === 'closed' ? ' — CLOSED' : ''));

  save();
  payLiabId = null;
  clMo('moLiabPay');
  renderLiabilities();
  // Also re-render cash since balance changed
  if (window.renderC) window.renderC();
  if (window.markDirty) window.markDirty('dash', 'cash');
  toast(d.name + ': ' + liabFmt(amt, d.currency) + ' paid' + (d.status === 'closed' ? ' — Liability closed!' : ''), 'a-g');
}

function clMoLiabPay() { payLiabId = null; clMo('moLiabPay'); }

// ── RENDER ──────────────────────────────────────────────────────
function renderLiabilities() {
  const liabs = S.liabilities.filter(l => l.status !== 'deleted');
  const noEl = document.getElementById('liabNoData');
  const bodyEl = document.getElementById('liabBody');
  const sumEl = document.getElementById('liabSum');

  if (!liabs.length) {
    if (noEl) noEl.style.display = '';
    if (bodyEl) bodyEl.innerHTML = '';
    if (sumEl) sumEl.innerHTML = '';
    return;
  }
  if (noEl) noEl.style.display = 'none';

  // Summary cards
  let totalTRY = 0, totalUSD = 0, openCount = 0, closedCount = 0;
  liabs.forEach(l => {
    const tryVal = liabToTRY(l.remainingAmount, l.currency) ?? 0;
    totalTRY += tryVal;
    totalUSD += liabToUSD(l.remainingAmount, l.currency) ?? 0;
    if (l.status === 'open') openCount++;
    else closedCount++;
  });

  const totalPaidTRY = liabs.reduce((s, l) => s + (l.payments || []).reduce((ps, p) => ps + (p.tryEquiv || 0), 0), 0);

  if (sumEl) sumEl.innerHTML = `
    <div class="neo-cell" style="border-left:3px solid var(--red)"><div class="l">Total Liabilities (TRY)</div><div class="v" style="color:var(--red)">${fT(totalTRY)}</div><div class="s">${fU(totalUSD)}</div></div>
    <div class="neo-cell" style="border-left:3px solid var(--grn)"><div class="l">Total Paid (TRY)</div><div class="v" style="color:var(--grn)">${fT(totalPaidTRY)}</div></div>
    <div class="neo-cell" style="border-left:3px solid var(--ora)"><div class="l">Open</div><div class="v">${openCount}</div><div class="s">${closedCount} closed</div></div>`;

  // Table
  if (bodyEl) bodyEl.innerHTML = liabs.map(l => {
    const tryVal = liabToTRY(l.remainingAmount, l.currency);
    const usdVal = liabToUSD(l.remainingAmount, l.currency);
    const paidPct = l.originalAmount > 0 ? Math.round((1 - l.remainingAmount / l.originalAmount) * 100) : 0;
    const statusBadge = l.status === 'closed'
      ? '<span class="badge b-closed" style="font-size:10px">Closed</span>'
      : '<span class="badge b-open" style="font-size:10px">Open</span>';
    const paidBar = `<div class="gls-track" style="border-radius:4px;height:6px;width:60px;display:inline-block;vertical-align:middle;margin-right:4px"><div style="background:var(--grn);border-radius:4px;height:6px;width:${paidPct}%"></div></div>${paidPct}%`;

    return `<tr>
      <td><strong style="cursor:pointer;color:var(--acc)" data-action="openLiability" data-args='["${l.id}"]'>${esc(l.name)}</strong>${l.note ? '<br><span style="font-size:10px;color:var(--t3)">' + esc(l.note) + '</span>' : ''}</td>
      <td>${l.currency}</td>
      <td class="tr">${liabFmt(l.originalAmount, l.currency)}</td>
      <td class="tr" style="color:${l.remainingAmount > 0 ? 'var(--red)' : 'var(--grn)'}">${liabFmt(l.remainingAmount, l.currency)}</td>
      <td class="tr">${tryVal > 0 ? fT(tryVal) : '—'}</td>
      <td class="tr">${usdVal > 0 ? fU(usdVal) : '—'}</td>
      <td class="tr">${paidBar}</td>
      <td>${statusBadge}</td>
      <td>${l.status === 'open' ? '<button class="btn btn-s btn-p" style="font-size:10px" data-action="openLiabPay" data-args=\'["' + l.id + '"]\'>Pay</button>' : ''}</td>
    </tr>${paymentRows(l)}`;
  }).join('');
}

/**
 * The payments, and an × for each.
 *
 * They were never rendered. `4e187ee` put a badge on the ledger row saying "undo it at
 * Liabilities" while this page showed only a name, a remaining amount and a Pay button that
 * disappears once the debt closes — so for both of his liabilities the door was not merely
 * broken, it was invisible. A control can be dead in three layers; this is the third.
 */
function paymentRows(l) {
  const ps = (l.payments || []).filter(Boolean);
  if (!ps.length) return '';
  return ps.map(p => {
    const linked = (S.cashTxs || []).some(tx => tx && tx._paymentId === p.id);
    // An unlinked payment cannot be reversed safely, and saying so here beats a refusal toast
    // after the click. v51 links every legacy row it can identify without guessing.
    const btn = linked
      ? `<button class="btn btn-s btn-d" style="font-size:9px;padding:1px 5px" data-action="deletePayment" data-args='["${l.id}","${p.id}"]' title="Undo this payment: cash back up, ledger row removed, remaining restored">×</button>`
      : '<span class="badge" style="font-size:9px;opacity:.7" title="No linked ledger row — reversing would move the balance with nothing to match it">no ledger link</span>';
    return `<tr style="opacity:.75">
      <td style="padding-left:18px;font-size:11px;color:var(--t3)">↳ payment ${esc(String(p.date || '').slice(0, 10))}${p.note && p.note !== 'Payment' ? ' — ' + esc(p.note) : ''}</td>
      <td style="font-size:11px">${esc(l.currency)}</td>
      <td class="tr" style="font-size:11px">${liabFmt(p.amount, l.currency)}</td>
      <td class="tr" style="font-size:11px">—</td>
      <td class="tr" style="font-size:11px">${fT(Number(p.tryEquiv) || 0)}</td>
      <td class="tr" style="font-size:11px">—</td>
      <td class="tr" style="font-size:11px">—</td>
      <td></td>
      <td>${btn}</td>
    </tr>`;
  }).join('');
}

// ── Total liabilities in USD (for dashboard net worth) ──────────
function totalLiabilitiesUSD() {
  return (S.liabilities || [])
    .filter(l => l.status !== 'deleted' && l.status !== 'closed')
    // An unconvertible liability must NOT silently contribute 0 — that overstates net
 // worth. Fall back to the raw amount so the debt still counts; the integrity watchdog
 // reports the missing FX rate separately. (audit 2026-08-01)
    .reduce((s, l) => s + (liabToUSD(l.remainingAmount, l.currency) ?? (l.remainingAmount || 0)), 0);
}

function totalLiabilitiesTRY() {
  return (S.liabilities || [])
    .filter(l => l.status !== 'deleted' && l.status !== 'closed')
    .reduce((s, l) => s + (liabToTRY(l.remainingAmount, l.currency) ?? (l.remainingAmount || 0)), 0);
}

// ── Exports ─────────────────────────────────────────────────────
export {
  openLiability, saveLiability, deleteLiability, clMoLiab,
  openLiabPay, execLiabPay, clMoLiabPay, deletePayment,
  renderLiabilities,
  totalLiabilitiesUSD, totalLiabilitiesTRY,
  liabToTRY, liabToUSD, liabFmt
};
